import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Play, Pause, SpinnerGap, Sparkle, Lightning, Lock, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthContext";
import AuthModal from "../auth/AuthModal";
import API from "../api";
import genPlayer from "../audio/GenPlayer";
import BillingModal from "../components/BillingModal";
import ThemeToggle from "../components/ThemeToggle";
import GlobalNav from "../components/GlobalNav";
import audioBufferToWav from "audiobuffer-to-wav";
import { toast } from "sonner";

// v10.3 — 6 active genres × distinct glow colors. Each tile pulses softly when selected.
const GENRES = [
  // v19.35 — Genre-specific default BPM (auto-applies on genre click).
  // Producer can still change the BPM slider after, but the click
  // pre-loads the sweet spot for each style.
  { id: "pop",     label: "Pop",     glow: "#22c55e", defaultBpm: 70  },  // green
  { id: "trap",    label: "Trap",    glow: "#a855f7", defaultBpm: 70  },  // purple
  { id: "rnb",     label: "RnB",     glow: "#ec4899", defaultBpm: 58  },  // pink
  { id: "country", label: "Country", glow: "#eab308", defaultBpm: 58  },  // yellow
  { id: "house",   label: "House",   glow: "#06b6d4", defaultBpm: 120 },  // cyan
  { id: "afro",    label: "Afro",    glow: "#f97316", defaultBpm: 55  },  // orange — Donnell's Afro templates are halftime
];

const readGenPrefs = () => {
  try {
    const p = JSON.parse(localStorage.getItem("crow_gen_prefs") || "null");
    if (p && GENRES.some((g) => g.id === p.genre)) return p;
  } catch { /* noop */ }
  return { genre: "pop", bpm: GENRES[0].defaultBpm };
};

const getDeviceId = () => {
  let id = localStorage.getItem("crow_device_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("crow_device_id", id);
  }
  return id;
};

const guestPreviewUsed = () => !!localStorage.getItem("crow_guest_gen_used");

export default function LandingPage() {
  const { user, ready } = useAuth();
  // Genre/BPM persist across reloads + the signup flow.
  const [selected, setSelected] = useState(() => readGenPrefs().genre);
  const [bpm, setBpm] = useState(() => Number(readGenPrefs().bpm) || 70);
  const [generating, setGenerating] = useState(false);
  const [pattern, setPattern] = useState(null);
  const [generationId, setGenerationId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [stepNow, setStepNow] = useState(-1);
  const [err, setErr] = useState("");
  const [openAuth, setOpenAuth] = useState(false);
  const [openBilling, setOpenBilling] = useState(false);
  const [authHeadline, setAuthHeadline] = useState("");
  const [isGuestResult, setIsGuestResult] = useState(false);
  const [showContinuation, setShowContinuation] = useState(false);
  const pendingActionRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    const off = genPlayer.subscribe(setStepNow);
    return () => off();
  }, []);

  useEffect(() => {
    localStorage.setItem("crow_gen_prefs", JSON.stringify({ genre: selected, bpm }));
  }, [selected, bpm]);

  // Auto-scroll to the freshly generated beat so it's never missed below the fold.
  useEffect(() => {
    if (!pattern || !resultRef.current) return;
    const t = setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
    return () => clearTimeout(t);
  }, [pattern]);

  const promptSignup = (headline = "") => {
    setAuthHeadline(headline);
    setOpenAuth(true);
  };

  const onAuthSuccess = (mode) => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action === "daw") { window.location.href = "/daw"; return; }
    if (mode === "signup") setShowContinuation(true);
  };

  const onGenerate = async () => {
    setErr("");
    const guest = !user;
    if (guest && guestPreviewUsed()) {
      promptSignup("Your free preview is used — create a free account to get 2 more beats.");
      return;
    }
    if (!guest && (user.credits ?? 0) <= 0) { setOpenBilling(true); return; }
    setGenerating(true);
    setPlaying(false);
    setPattern(null);          // v19.8 — clear the previous pattern so the
    setGenerationId(null);     //          Play button never appears while
    genPlayer.stop();          //          samples are still decoding.
    try {
      const { data } = guest
        ? await API.post("/ai/generate-guest", { genre: selected, bpm, device_id: getDeviceId() })
        : await API.post("/ai/generate", { genre: selected, bpm });
      // v19.8 P0 FIX — the previous order was:
      //   1) setPattern(data.pattern)      ← UI renders Play button
      //   2) await genPlayer.loadPattern(…) ← samples still decoding
      // A tester who clicked Play between (1) and (2) hit the
      // `if (!this.pattern) return;` guard in GenPlayer and heard
      // NOTHING — the click was a silent no-op. Second click worked
      // because hydration had finished by then. Fix: load ALL samples
      // FIRST, only THEN reveal the Play button so it never fires into
      // a half-hydrated engine.
      await genPlayer.loadPattern(data.pattern, bpm);
      setPattern(data.pattern);
      setIsGuestResult(guest);
      setGenerationId(data.generation_id || data.id || null);
      if (guest) localStorage.setItem("crow_guest_gen_used", "1");
      else window.dispatchEvent(new CustomEvent("crow:credits-changed", { detail: data.user }));
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) { setOpenBilling(true); return; }
      if (guest && e.response?.status === 403) {
        localStorage.setItem("crow_guest_gen_used", "1");
        promptSignup(typeof detail === "string" ? detail : "");
        return;
      }
      setErr(typeof detail === "string" ? detail : "Generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  // v9.9 — Jump into the DAW with THIS generation. Stashes the full
  // pattern (incl. components map) so the DAW hydrator can rebuild
  // Sampler + Piano-Roll tracks that sound identical to what's playing.
  const openInDaw = () => {
    if (!pattern) return;
    genPlayer.stop();
    setPlaying(false);
    sessionStorage.setItem("crow_incoming_pattern", JSON.stringify({
      pattern,
      bpm,
      genre: selected,
      generation_id: generationId,
    }));
    if (!user) {
      pendingActionRef.current = "daw";
      promptSignup("Create a free account to open this beat in the DAW.");
      return;
    }
    window.location.href = "/daw";
  };

  const guestLocked = isGuestResult && !user;

  const togglePlay = async () => {
    if (!pattern) return;
    if (playing) { genPlayer.stop(); setPlaying(false); }
    else { await genPlayer.play(); setPlaying(true); }
  };

  const accent = GENRES.find((g) => g.id === selected)?.glow ?? "#fff";

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden"
      style={{ background: "#0a0a0a" }}
      data-testid="landing"
    >
      {/* Thick bright-white grid background (the high-contrast signature look) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.07) 2px, transparent 2px), linear-gradient(90deg, rgba(255,255,255,0.07) 2px, transparent 2px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 30%, ${accent}22, transparent 60%)`,
          transition: "background 400ms ease",
        }}
      />

      {/* v19.30 — Post-purchase upsell banner. Shown when the user
          lands here from /purchase/success/… (or their email link). */}
      <PostPurchaseBanner />

      {/* Top nav */}
      <header className="relative z-40 flex items-center justify-between gap-2 px-4 sm:px-6 py-4" data-testid="landing-header">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img
            src="/crow-standing.jpg"
            alt="CROW"
            width={36}
            height={36}
            style={{ display: "block", objectFit: "contain", borderRadius: 6, flexShrink: 0 }}
            data-testid="landing-crow-logo"
          />
          <div className="font-display font-extrabold text-xl sm:text-2xl tracking-tight whitespace-nowrap hidden min-[360px]:block">C.R.O.W.</div>
          <span className="hidden md:inline text-[10px] uppercase tracking-wider text-white/40 font-mono">real melodies · real producers</span>
        </div>
        <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
          <GlobalNav />
          <a
            href="/daw"
            onClick={(e) => {
              // v19.0 — Gate the DAW behind sign-in from the landing entry
              // point. Guests get the AuthModal immediately instead of
              // being bounced to a full-screen sign-in prompt on /daw.
              if (!user) {
                e.preventDefault();
                promptSignup();
              }
            }}
            className="hidden sm:flex text-xs font-mono text-white/70 hover:text-white transition items-center gap-1 whitespace-nowrap"
            data-testid="link-daw"
          >
            C.R.O.W. DAW
          </a>
          <ThemeToggle />
          <PersonIcon openAuth={() => promptSignup()} openBilling={() => setOpenBilling(true)} />
        </nav>
      </header>

      {/* Hero */}
      <main
        className="relative z-10 max-w-5xl mx-auto px-6 pt-10 pb-6 sm:pb-10"
        style={{
          // iOS Safari's bottom toolbar can cover ~80px of the viewport when
          // it slides in on scroll. `env(safe-area-inset-bottom)` is 0 on
          // desktop and up to ~34px on notched iPhones — we add extra room
          // on top so the Play button is never obscured by browser chrome.
          paddingBottom: "max(160px, calc(env(safe-area-inset-bottom, 0px) + 160px))",
        }}
      >
        <div className="text-center mb-10">
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight">
            Generate a beat in <span style={{ color: accent, textShadow: `0 0 24px ${accent}99` }}>seconds</span>.
          </h1>
          <p className="text-white/50 mt-3 max-w-2xl mx-auto text-sm">
            Utilizing real melodies and drums from real producers to make its generations.
          </p>
        </div>

        {/* Genre grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8" data-testid="genre-grid">
          {GENRES.map((g) => {
            const active = g.id === selected;
            return (
              <button
                key={g.id}
                onClick={() => {
                  setSelected(g.id);
                  // v19.35 — Auto-apply the genre's default BPM on click.
                  // Producer can still nudge the slider afterward.
                  if (g.defaultBpm) setBpm(g.defaultBpm);
                }}
                className="relative rounded-xl p-5 transition-all"
                style={{
                  background: active ? "#0e0e0e" : "#0a0a0a",
                  border: `1.5px solid ${active ? g.glow : "#1a1a1a"}`,
                  boxShadow: active
                    ? `0 0 0 1px ${g.glow}66, 0 0 28px ${g.glow}aa, inset 0 0 18px ${g.glow}33`
                    : `0 0 12px ${g.glow}33`,
                  transform: active ? "translateY(-2px)" : "translateY(0)",
                }}
                data-testid={`genre-${g.id}`}
              >
                <div
                  className="absolute inset-0 rounded-xl pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at 50% 100%, ${g.glow}33, transparent 70%)`,
                    opacity: active ? 1 : 0.5,
                  }}
                />
                <div className="relative font-display font-extrabold text-lg" style={{ color: g.glow, textShadow: `0 0 12px ${g.glow}80` }}>
                  {g.label}
                </div>
                <div className="relative text-[10px] font-mono mt-1 text-white/40">{g.id}</div>
              </button>
            );
          })}
        </div>

        {/* BPM + Generate */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <label className="flex items-center gap-2 text-xs font-mono text-white/50">
            BPM
            <input
              type="number" min={40} max={220} value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
              className="w-20 px-2 py-1.5 rounded-md bg-[#0a0a0a] border border-[#262626] text-white text-sm focus:outline-none focus:border-white/40"
              data-testid="bpm-input"
            />
          </label>
          <button
            onClick={onGenerate}
            disabled={generating}
            className="relative px-8 py-3 rounded-full font-display font-extrabold text-base tracking-wide transition-all"
            style={{
              background: "linear-gradient(180deg,#fff,#d4d4d4)",
              color: "#0a0a0a",
              boxShadow: `0 0 0 1.5px ${accent}, 0 0 32px ${accent}88, 0 6px 16px rgba(0,0,0,0.6)`,
              transform: generating ? "scale(0.97)" : "scale(1)",
            }}
            data-testid="generate-btn"
          >
            {generating ? (
              <span className="flex items-center gap-2"><SpinnerGap size={18} weight="bold" className="animate-spin" /> Generating…</span>
            ) : (
              <span className="flex items-center gap-2"><Sparkle size={16} weight="fill" /> Generate</span>
            )}
          </button>
        </div>

        {!user && ready && !guestPreviewUsed() && !pattern && (
          <div className="text-center text-[11px] font-mono text-white/50 -mt-2 mb-6" data-testid="guest-free-note">
            1 free preview · no account needed
          </div>
        )}

        {err && <div className="text-center text-sm text-red-400 mb-4" data-testid="generate-error">{err}</div>}

        {/* Beat preview — sleek waveform + ambient pulse, no toy-like grid.
            v9.9.6 — MOBILE FIRST rebuilt: on ≤640px screens the header +
            secondary actions stack above the waveform, and the primary
            Play/Stop control becomes a full-width thumb-friendly button
            below so it's ALWAYS visible in portrait without rotation. */}
        {pattern && (
          <div
            ref={resultRef}
            className="rounded-2xl p-4 sm:p-6 scroll-mt-24"
            style={{ background: "#080808", border: `1.5px solid ${accent}55`, boxShadow: `0 0 36px ${accent}33` }}
            data-testid="pattern-preview"
          >
            {guestLocked && (
              <GuestPreviewBanner accent={accent} onSignup={() => promptSignup("Save this beat, export it, and get 2 more free generations.")} />
            )}
            {/* Header row — stacks on mobile, splits on ≥640px */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-wider text-white/40">Generated beat</div>
                <div className="font-display font-bold text-base sm:text-lg truncate">{GENRES.find((g) => g.id === selected)?.label} · {bpm} BPM · key {pattern.key || "C"} {pattern.mode || "minor"}</div>
              </div>
              {/* Secondary actions (Save / Export / Edit in DAW) — wrap freely
                  on tiny screens; sit inline on desktop. Do NOT include the
                  Play button here — it lives below the waveform for
                  guaranteed mobile visibility. */}
              <div className="flex flex-wrap items-center gap-2">
                <SaveBeatButton pattern={pattern} bpm={bpm} genre={selected} accent={accent} requireAuth={() => promptSignup("Create a free account to save this beat.")} />
                <ExportButton bpm={bpm} accent={accent} locked={guestLocked} requireAuth={() => promptSignup("Create a free account to export this beat as a WAV.")} />
                <button
                  onClick={openInDaw}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-full font-bold text-xs sm:text-sm flex items-center gap-2 transition"
                  style={{ background: "#0a0a0a", color: "#93c5fd", border: `1.5px solid ${accent}77` }}
                  data-testid="edit-in-daw-btn"
                  title="Open this exact interchange in the C.R.O.W. DAW — 1:1 sound"
                >
                  ✎ Edit in DAW
                </button>
              </div>
            </div>

            {/* Sleek waveform — compact height on mobile (120px) so the Play
                button always sits above the browser chrome; full 200px on
                tablet+ where vertical space is plentiful. */}
            <SleekWaveform accent={accent} playing={playing} compact />

            {/* PRIMARY control — Play / Stop. Full-width thumb target on
                mobile (56px tall) so a producer never has to hunt for it
                or rotate their phone. Sits inside the card so scrolling
                to the beat guarantees it's in the viewport. */}
            <button
              onClick={togglePlay}
              className="mt-4 w-full sm:w-fit sm:min-w-[200px] sm:mx-auto rounded-full font-display font-extrabold text-base tracking-wide flex items-center justify-center gap-2 transition"
              style={{
                background: accent,
                color: "#000",
                padding: "16px 24px",
                boxShadow: `0 0 22px ${accent}aa, 0 8px 24px rgba(0,0,0,0.55)`,
                minHeight: 56,
              }}
              data-testid="play-pattern-btn"
              aria-label={playing ? "Stop playback" : "Play generated beat"}
            >
              {playing ? <><Pause size={18} weight="fill" /> Stop</> : <><Play size={18} weight="fill" /> Play beat</>}
            </button>
          </div>
        )}
      </main>

      <SignupContinuation
        open={showContinuation && !!user}
        credits={user?.credits ?? 2}
        genreLabel={GENRES.find((g) => g.id === selected)?.label}
        bpm={bpm}
        accent={accent}
        onGenerate={() => { setShowContinuation(false); onGenerate(); }}
        onClose={() => setShowContinuation(false)}
      />
      <AuthModal open={openAuth} onClose={() => setOpenAuth(false)} onSuccess={onAuthSuccess} headline={authHeadline} />
      <BillingModal open={openBilling} onClose={() => setOpenBilling(false)} />
    </div>
  );
}

// Top-right person icon with credit badge. Tap → modal (signed-out) or dropdown (signed-in).
function PersonIcon({ openAuth, openBilling }) {
  const { user, ready, refresh, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  // Listen for credit changes from anywhere in the app
  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener("crow:credits-changed", onChange);
    return () => window.removeEventListener("crow:credits-changed", onChange);
  }, [refresh]);

  if (!ready) return null;

  if (!user) {
    return (
      <button
        onClick={openAuth}
        className="w-10 h-10 rounded-full border flex items-center justify-center transition shrink-0"
        style={{ background: "#0a0a0a", borderColor: "#2a2a2a", color: "white" }}
        data-testid="person-icon"
        aria-label="Sign in"
      >
        <User size={18} weight="bold" />
      </button>
    );
  }

  const credits = user.credits ?? 0;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 sm:gap-2 h-10 px-2.5 sm:px-3 rounded-full transition whitespace-nowrap shrink-0"
        style={{ background: "#0a0a0a", border: "1.5px solid #2a2a2a" }}
        data-testid="person-icon"
        aria-label="Profile and credits"
      >
        <User size={16} weight="bold" />
        <span className="text-xs font-mono">
          <span className="text-emerald-300 font-bold" data-testid="credit-badge">
            {credits >= 10000 ? new Intl.NumberFormat("en", { notation: "compact" }).format(credits) : credits}
          </span>
          <span className="hidden sm:inline text-white/40"> credits</span>
        </span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-12 w-72 max-w-[calc(100vw-24px)] rounded-xl p-4"
          style={{
            background: "#0c0c0c",
            border: "1px solid #2a2a2a",
            boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
            // v10.1 — force the dropdown ABOVE every landing-page element
            // (genre buttons, waveform, banner) on mobile Safari. z-50
            // (=50) was tying with sibling absolute panels; z-index 200
            // sits above everything short of the auth/billing modals
            // (z-[1000]) which SHOULD dominate a dropdown by design.
            zIndex: 200,
          }}
          data-testid="person-menu"
        >
          <div className="text-sm font-semibold mb-1">{user.username || user.email}</div>
          <div className="text-[11px] text-white/40 font-mono mb-3">{user.email}</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Stat label="Credits" value={credits} highlight />
            <Stat label="Generated" value={user.total_generations ?? 0} />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-white/40 font-mono mb-2">subscription</div>
          <div className="text-sm mb-3 capitalize">{user.plan === "studio" ? "Studio (admin)" : (user.plan || "free")}</div>
          <button
            onClick={() => { setOpen(false); openBilling(); }}
            className="w-full py-2 rounded-md text-sm font-bold mb-2"
            style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#0a0a0a", border: "1px solid #303030" }}
            data-testid="buy-credits-btn"
          >
            <Lightning size={12} weight="fill" className="inline mr-1" /> Buy more credits
          </button>
          {isAdmin && (
            <button onClick={() => { setOpen(false); nav("/training"); }} className="w-full py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] text-white/80 hover:text-white mb-2" data-testid="admin-training-link">
              🧪 Training Ground
            </button>
          )}
          <button onClick={() => { setOpen(false); nav("/profile"); }} className="w-full py-2 rounded-md text-sm bg-[#0a0a0a] border border-[#262626] text-white/80 hover:text-white mb-2">
            Profile
          </button>
          <button onClick={() => { setOpen(false); logout(); }} className="w-full py-1.5 rounded-md text-xs text-red-300/80 hover:text-red-300">
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-md p-2 text-center" style={{ background: "#080808", border: "1px solid #1a1a1a" }}>
      <div className="text-[9px] uppercase tracking-wider text-white/40 font-mono">{label}</div>
      <div className="text-base font-extrabold" style={{ color: highlight ? "#34d399" : "#fff" }} data-testid={`stat-${label.toLowerCase()}`}>{value}</div>
    </div>
  );
}

// Sleek minimal waveform visualizer — single gradient sine reading from the
// master waveform tap on GenPlayer. When idle (no playback) shows a slow
// breathing line so the panel still feels alive. No grid cells, no toy vibes.
function SleekWaveform({ accent, playing, compact = false }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const startRef = useRef(performance.now());
  // v12.6 — Live-track theme so the visualizer flips background/stroke
  // colors when the user toggles light/dark WITHOUT reloading. Reads
  // the initial value from <html data-theme> and observes changes via
  // both a MutationObserver AND the custom `crow-theme-change` event
  // dispatched by ThemeToggle.
  const [themeState, setThemeState] = useState(() => document.documentElement.getAttribute("data-theme") || "dark");
  useEffect(() => {
    const readTheme = () => setThemeState(document.documentElement.getAttribute("data-theme") || "dark");
    const mo = new MutationObserver(readTheme);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    window.addEventListener("crow-theme-change", readTheme);
    return () => { mo.disconnect(); window.removeEventListener("crow-theme-change", readTheme); };
  }, []);
  const isLight = themeState === "light";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const draw = () => {
      const w = canvas.width = canvas.clientWidth;
      const h = canvas.height = canvas.clientHeight;
      // v12.6 — Theme-aware canvas fill. NOTE: the App's <html> element
      // gets a global CSS invert filter in light mode, so drawing dark
      // colors here would render as WHITE on screen after inversion.
      // We continue to fill with dark colors — the invert filter flips
      // them to the intended light-mode appearance automatically. This
      // matches how every other DAW panel looks after the theme flip.
      // For the stroke, we bump the accent alpha in light mode so the
      // waveform stays visible against the inverted background.
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, w, h);
      const grad = ctx.createLinearGradient(0, h * 0.4, 0, h);
      grad.addColorStop(0, "transparent");
      grad.addColorStop(1, accent + (isLight ? "44" : "22"));
      ctx.fillStyle = grad;
      ctx.fillRect(0, h * 0.4, w, h * 0.6);

      const data = genPlayer.getWaveform?.();
      const t = (performance.now() - startRef.current) / 1000;
      ctx.lineWidth = isLight ? 2.2 : 1.6;
      ctx.shadowColor = accent;
      ctx.shadowBlur = playing ? 18 : 6;

      const lineGrad = ctx.createLinearGradient(0, 0, w, 0);
      lineGrad.addColorStop(0,   accent + "00");
      lineGrad.addColorStop(0.2, accent + "ff");
      lineGrad.addColorStop(0.8, accent + "ff");
      lineGrad.addColorStop(1,   accent + "00");
      ctx.strokeStyle = lineGrad;

      ctx.beginPath();
      if (data && playing) {
        for (let i = 0; i < data.length; i++) {
          const x = (i / data.length) * w;
          const y = h / 2 - data[i] * (h / 2) * 0.85;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
      } else {
        const amp = h * 0.06 * (1 + 0.6 * Math.sin(t * 1.4));
        const freq = 2.2;
        const N = 220;
        for (let i = 0; i <= N; i++) {
          const x = (i / N) * w;
          const phase = (i / N) * Math.PI * 2 * freq + t * 0.9;
          const y = h / 2 + Math.sin(phase) * amp + Math.sin(phase * 0.5) * amp * 0.3;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [accent, playing, isLight]);

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        height: compact ? "clamp(120px, 22vh, 200px)" : 200,
        background: "#040404",
        border: "1px solid #141414",
      }}
      data-testid="sleek-waveform"
      data-theme-current={themeState}
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}

// Export the generated beat to a WAV file via Tone.Offline. Renders a 40-bar
// structured arrangement (4 intro · 8 main · 8 pitched · 8 main · 8 pitched ·
// 4 outro) — defined in GenPlayer.renderToBuffer.
function ExportButton({ bpm, accent, locked, requireAuth }) {
  const [busy, setBusy] = useState(false);
  const onExport = async () => {
    if (busy) return;
    if (locked) { requireAuth?.(); return; }
    setBusy(true);
    try {
      const buf = await genPlayer.renderToBuffer({ sampleRate: 44100 });
      if (!buf || typeof buf.getChannelData !== "function") {
        throw new Error("Render did not return a valid AudioBuffer");
      }
      const wav = audioBufferToWav(buf);
      const blob = new Blob([wav], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crow-beat-${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`WAV exported · ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
    } catch (e) {
      console.error("[ExportWAV]", e);
      toast.error("Export failed: " + (e?.message || "unknown error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onExport}
      disabled={busy}
      className="px-4 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 transition"
      style={{
        background: "#0a0a0a",
        color: "#fff",
        border: `1.5px solid ${accent}88`,
        boxShadow: `0 0 14px ${accent}33`,
      }}
      data-testid="export-wav-btn"
      title="Render the beat to a WAV file"
    >
      {busy ? <><SpinnerGap size={14} weight="bold" className="animate-spin" /> Rendering…</> : <><DownloadSimple size={14} weight="bold" /> Export WAV</>}
    </button>
  );
}

// Save the current pattern to the user's "Saved Beats" library. Anonymous
// users are bounced through the auth modal first.
function SaveBeatButton({ pattern, bpm, genre, accent, requireAuth }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  // v19.29 — Dice suggester wired to /api/store/beats/suggest-names.
  // Returns 10 short all-caps street/vibe names via Claude (Universal
  // key). Silently falls back to a curated list if the LLM is down.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const onClick = () => {
    if (!user) { requireAuth?.(); return; }
    const def = `${GENRES.find((g) => g.id === genre)?.label || "Beat"} · ${bpm} BPM`;
    setName(def);
    setOpen(true);
  };
  const rollDice = async () => {
    if (suggesting) return;
    setSuggesting(true);
    try {
      const { data } = await API.post("/store/beats/suggest-names", { genre, mood: "street" });
      setSuggestions(data.names || []);
      if ((data.names || []).length && !name.trim().match(/^[A-Z]{2,}(\s[A-Z]{2,})?$/)) {
        // Auto-populate the first name only when the field still
        // holds the default "Genre · BPM" placeholder so we don't
        // clobber whatever the producer has already typed.
        setName(data.names[0]);
      }
    } catch { toast.error("Could not fetch names"); }
    finally { setSuggesting(false); }
  };
  const onSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await API.post("/saved-beats", { name: name.trim() || "Untitled Beat", pattern, bpm, genre });
      toast.success(`Saved "${data.beat.name}" to your library · view it in your profile`);
      setOpen(false);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={onClick}
        className="px-4 py-2 rounded-full font-bold text-sm flex items-center gap-1.5 transition"
        style={{
          background: "rgba(255,255,255,0.04)",
          color: accent,
          border: `1.5px solid ${accent}88`,
          boxShadow: `0 0 14px ${accent}33`,
        }}
        data-testid="save-beat-btn"
        title="Save this beat to your library"
      >
        <FloppyDisk size={14} weight="fill" /> Save
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-6"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
          onClick={() => !busy && setOpen(false)}
          data-testid="save-beat-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 space-y-4"
            style={{ background: "#0c0c0c", border: `1.5px solid ${accent}55`, boxShadow: `0 0 32px ${accent}33` }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-bold text-lg">Save beat to your library</h3>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSave(); }}
                maxLength={80}
                placeholder="Beat name — e.g. SLEEPY"
                className="flex-1 px-3 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm"
                autoFocus
                data-testid="save-beat-name-input"
              />
              <button
                type="button"
                onClick={rollDice}
                disabled={suggesting}
                className="px-2.5 py-2 rounded-md text-sm border border-[#262626] hover:border-emerald-400/40 hover:text-emerald-300 disabled:opacity-50"
                title="Suggest short vibe-style beat names"
                data-testid="save-beat-dice"
              >
                {suggesting ? <SpinnerGap size={14} weight="bold" className="animate-spin" /> : "🎲"}
              </button>
            </div>
            {suggestions.length > 0 ? (
              <div className="flex flex-wrap gap-1.5" data-testid="save-beat-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setName(s)}
                    className="px-2 py-1 rounded font-mono text-[11px] bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-300"
                  >{s}</button>
                ))}
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy} className="px-4 py-2 rounded-md text-sm font-bold text-white/70 hover:text-white">Cancel</button>
              <button
                onClick={onSave}
                disabled={busy}
                className="px-4 py-2 rounded-md text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: accent, color: "#000" }}
                data-testid="save-beat-confirm"
              >
                {busy ? <><SpinnerGap size={14} weight="bold" className="animate-spin" /> Saving…</> : <><FloppyDisk size={14} weight="fill" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// v19.30 — Post-purchase upsell banner. Reads the beat context from
// sessionStorage (written by /purchase/success page) plus the
// `?post_purchase=1` query flag so the buyer's next screen after
// clicking "Make your own beat in this style" is the generator itself
// with a clear message. Dismisses on X or after 20 seconds.
function PostPurchaseBanner() {
  const [visible, setVisible] = React.useState(false);
  const [ctx, setCtx] = React.useState(null);
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = params.get("post_purchase") === "1";
      const raw = sessionStorage.getItem("crow_post_purchase");
      if (flag && raw) {
        setCtx(JSON.parse(raw));
        setVisible(true);
        // Clear the query flag so a refresh doesn't re-show the banner.
        try {
          params.delete("post_purchase");
          const next = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
          window.history.replaceState({}, "", next);
        } catch { /* noop */ }
        const t = setTimeout(() => setVisible(false), 20000);
        return () => clearTimeout(t);
      }
    } catch { /* noop */ }
  }, []);
  if (!visible || !ctx) return null;
  return (
    <div className="relative z-50 mx-4 mt-3 rounded-2xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 px-4 py-3 flex items-center gap-3" data-testid="post-purchase-banner">
      <div className="text-2xl">🎧</div>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-bold">Your "{ctx.beat_name}" is downloading — now make your own in this style.</div>
        <div className="text-white/70 text-xs">2 free generations added to your account. Pick a genre below and hit Generate.</div>
      </div>
      <button onClick={() => setVisible(false)} className="text-white/60 hover:text-white text-xs font-bold px-2 py-1" data-testid="post-purchase-dismiss">✕</button>
    </div>
  );
}

function GuestPreviewBanner({ accent, onSignup }) {
  return (
    <div
      className="mb-4 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
      style={{ background: `${accent}12`, border: `1px solid ${accent}55` }}
      data-testid="guest-preview-banner"
    >
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-bold flex items-center gap-1.5"><Lock size={13} weight="fill" /> Free preview</div>
        <div className="text-white/60 text-xs">Create a free account to save, export &amp; edit this beat — plus 2 more free generations.</div>
      </div>
      <button
        onClick={onSignup}
        className="px-4 py-2 rounded-full text-xs font-black shrink-0"
        style={{ background: accent, color: "#000" }}
        data-testid="guest-preview-signup-btn"
      >
        Create free account
      </button>
    </div>
  );
}

function SignupContinuation({ open, credits, genreLabel, bpm, accent, onGenerate, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)" }}
      data-testid="signup-continuation"
    >
      <div
        className="w-[min(460px,94vw)] rounded-2xl p-7 text-center"
        style={{ background: "#0b0b0b", border: `1.5px solid ${accent}77`, boxShadow: `0 0 48px ${accent}33` }}
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mb-3">Account created</div>
        <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight" data-testid="signup-continuation-title">
          Your {credits} free beats are ready — hit Generate.
        </h2>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono" style={{ border: `1px solid ${accent}66`, color: accent }} data-testid="signup-continuation-selection">
          {genreLabel} · {bpm} BPM
        </div>
        <button
          onClick={onGenerate}
          className="mt-6 w-full py-3.5 rounded-full font-display font-extrabold text-base flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#0a0a0a", boxShadow: `0 0 0 1.5px ${accent}, 0 0 32px ${accent}88` }}
          data-testid="signup-continuation-generate-btn"
        >
          <Sparkle size={16} weight="fill" /> Generate
        </button>
        <button onClick={onClose} className="mt-3 text-xs text-white/50 hover:text-white underline" data-testid="signup-continuation-change">
          Change genre or BPM first
        </button>
      </div>
    </div>
  );
}
